import { FC, PropsWithChildren, useEffect, useMemo } from 'react';

import ServerSentEventsContext, { IServerSentEventOnConnect, IServerSentEventOnException, IServerSentEventOnJoin, IServerSentEventOnLeave, IServerSentEventOnMessage, IServerSentEventOnReconnect, IServerSentEventOnTick, IServerSentEventOnUpdate, IServerSentEventUnsubscribe, IServerSentEventsContext, IServerSentEventsListeners } from '@/contexts/api/ServerSentEventsContext';
import { ServerEventConnect, ServerEventJoin, ServerEventLeave, ServerEventMessage, ServerEventUpdate, ServerEventsClient } from '@servicestack/client';


interface IServerSentEventsProviderProps {
    baseUrl: string;
    debug?: boolean;
}

class ServerSentEventsManager implements IServerSentEventsContext {
    private listeners: Record<string, IServerSentEventsListeners> = {};
    private client?: ServerEventsClient;
    private baseUrl: string;
    private debug: boolean;

    constructor(baseUrl: string, debug: boolean) {
        this.baseUrl = baseUrl;
        this.debug = debug;
    }

    private getListeners = <TListener = Function>(type: keyof IServerSentEventsListeners, channel?: string): TListener[] => {
        if (!channel) {
            return Object.keys(this.listeners).flatMap(x => this.listeners[x][type] as unknown as TListener[]);
        }

        return this.listeners[channel]?.[type] as unknown as TListener[] ?? [];
    }

    private createClient = () => {
        this.client = new ServerEventsClient(this.baseUrl, Object.keys(this.listeners), {
            handlers: {
                onConnect: (sub:ServerEventConnect) => {
                    if (this.debug) {
                        console.log('ServerSentEventsManager - onConnect', sub);
                    }

                    for (let listener of this.getListeners<IServerSentEventOnConnect>('onConnect', sub?.channel)) {
                        listener(sub);
                    }
                },
                onJoin: (msg:ServerEventJoin) => {
                    if (this.debug) {
                        console.log('ServerSentEventsManager - onJoin', msg);
                    }

                    for (let listener of this.getListeners<IServerSentEventOnJoin>('onJoin', msg?.channel)) {
                        listener(msg);
                    }
                },
                onLeave: (msg:ServerEventLeave) => {
                    if (this.debug) {
                        console.log('ServerSentEventsManager - onLeave', msg);
                    }

                    for (let listener of this.getListeners<IServerSentEventOnLeave>('onLeave', msg?.channel)) {
                        listener(msg);
                    }
                },   
                onUpdate: (msg: ServerEventUpdate) => {
                    if (this.debug) {
                        console.log('ServerSentEventsManager - onUpdate', msg);
                    }

                    for (let listener of this.getListeners<IServerSentEventOnUpdate>('onUpdate', msg?.channel)) {
                        listener(msg);
                    }

                },
                onMessage: (msg: ServerEventMessage) => {
                    if (this.debug) {
                        console.log('ServerSentEventsManager - onMessage', msg);
                    }

                    for (let listener of this.getListeners<IServerSentEventOnMessage>('onMessage', msg?.channel)) {
                        listener(msg?.body, msg);
                    }
                }
            },
            onException: (error: any) => {
                if (this.debug) {
                    console.log('ServerSentEventsManager - onException', error);
                }

                for (let listener of this.getListeners<IServerSentEventOnException>('onException', undefined)) {
                    listener(error);
                }
            },
            onReconnect: (error: any) => {
                if (this.debug) {
                    console.log('ServerSentEventsManager - onReconnect', error);
                }

                for (let listener of this.getListeners<IServerSentEventOnReconnect>('onReconnect', undefined)) {
                    listener(error);
                }

            },
            onTick: (msg: ServerEventMessage) => {
                if (this.debug) {
                    console.log('ServerSentEventsManager - onTick', msg);
                }

                for (let listener of this.getListeners<IServerSentEventOnTick>('onTick', msg?.channel)) {
                    listener(msg);
                }
            }
        }).start();

        if (this.debug) {
            console.log('ServerSentEventsManager - Created Client', this.client);
        }
    }

    private disposeClient = () => {
        if (!this.client) {
            return;
        }

        if (this.debug) {
            console.log('ServerSentEventsManager - Disposed of Client', this.client);
        }

        this.client.stop();
        this.client = undefined;
    }

    private subscriptionDelay = 0;

    private subscriptionTimer: number | undefined =  undefined;

    private updateSubscription = () => {
        const channels = Object.keys(this.listeners);

        if (!this.client) {
            if (channels.length > 0) {

                if (this.debug) {
                    console.log('ServerSentEventsManager - Update Subscription', channels);
                }

                // subscriptions but no client - create client
                this.createClient();
                return;
            }

            // no subscripts and no client - done;
            return;
        }

        if (channels.length === 0) {

            if (this.debug) {
                console.log('ServerSentEventsManager - Update Subscription', channels);
            }

            this.disposeClient();
            return;
        }

        const prevChannels = this.client.channels;

        const newChannels = channels.filter(x => !prevChannels.includes(x));
        const oldChannels = prevChannels.filter(x => !channels.includes(x));

        if (newChannels.length > 0 || oldChannels.length > 0) {

            if (this.debug) {
                console.log('ServerSentEventsManager - Update Subscription', channels);
            }
            
            this.client.subscribeToChannels(...newChannels);
            this.client.unsubscribeFromChannels(...oldChannels);
        }
    };

    //@ts-expect-error
    private subscribe = (channel: string) => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = window.setTimeout(() => this.updateSubscription(), this.subscriptionDelay);
    }

    //@ts-expect-error
    private unsubscribe = (channel: string) => {
        if (this.subscriptionTimer) {
            window.clearTimeout(this.subscriptionTimer);
        }

        this.subscriptionTimer = window.setTimeout(() => this.updateSubscription(), this.subscriptionDelay);
    }

    /* private subscribe = (channel: string) => {
        if (!this.client) {
            this.createClient();
            return;
        }

        if (this.client.channels.includes(channel)) {
            return;
        }

        this.client.subscribeToChannels(channel);

        if (this.debug) {
            console.log('ServerSentEventsManager - Subscribed to Channel', channel);
        }
    }

    private unsubscribe = (channel: string) => {
        if (!this.client) {
            return;
        }

        if (!this.client.channels.includes(channel)) {
            return;
        }

        if (this.client.channels.length === 1) {
            this.disposeClient();
            return;
        }

        this.client.unsubscribeFromChannels(channel);

        if (this.debug) {
            console.log('ServerSentEventsManager - Unsubscribed from Channel', channel);
        }
    } */

    private ensureChannel = (channel: string) => {
        if (this.listeners[channel] !== undefined) {
            return;
        }

        this.listeners[channel] = {
            onConnect: [],
            onException: [],
            onJoin: [],
            onLeave: [],
            onMessage: [],
            onReconnect: [],
            onTick: [],
            onUpdate: []
        }

        this.subscribe(channel);
    }

    private cleanChannel = (channel: string) => {
        if (this.listeners[channel] === undefined) {
            return;
        }

        this.listeners[channel].onConnect ??= [];
        this.listeners[channel].onException ??= [];
        this.listeners[channel].onJoin ??= [];
        this.listeners[channel].onLeave ??= [];
        this.listeners[channel].onMessage ??= [];
        this.listeners[channel].onReconnect ??= [];
        this.listeners[channel].onTick ??= [];
        this.listeners[channel].onUpdate ??= [];

        if (
            this.listeners[channel].onConnect.length === 0 &&
            this.listeners[channel].onException.length === 0 &&
            this.listeners[channel].onJoin.length === 0 &&
            this.listeners[channel].onLeave.length === 0 &&
            this.listeners[channel].onMessage.length === 0 &&
            this.listeners[channel].onReconnect.length === 0 &&
            this.listeners[channel].onTick.length === 0 &&
            this.listeners[channel].onUpdate.length === 0
        )
        {
            delete this.listeners[channel];
            this.unsubscribe(channel);
        }
    }

    public addOnConnectListener = (
        channel: string, 
        listener: IServerSentEventOnConnect
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onConnect.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onConnect = this.listeners[channel].onConnect.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnJoinListener = (
        channel: string, 
        listener: IServerSentEventOnJoin
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onJoin.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onJoin = this.listeners[channel].onJoin.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnLeaveListener = (
        channel: string, 
        listener: IServerSentEventOnLeave
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onLeave.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onLeave = this.listeners[channel].onLeave.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnUpdateListener = (
        channel: string, 
        listener: IServerSentEventOnUpdate
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onUpdate.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onUpdate = this.listeners[channel].onUpdate.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnMessageListener = <TData = any>(
        channel: string, 
        listener: IServerSentEventOnMessage<TData>
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onMessage.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onMessage = this.listeners[channel].onMessage.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    /* public addOnMessageListeners = <TData = any>(
        listeners: Record<string, IServerSentEventOnMessage<TData>[]>
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onMessage.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onMessage = this.listeners[channel].onMessage.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    }; */

    public addOnExceptionListener = <TError = any>(
        channel: string, 
        listener: IServerSentEventOnException<TError>
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onException.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onException = this.listeners[channel].onException.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnReconnectListener =<TError = any>(
        channel: string, 
        listener: IServerSentEventOnReconnect<TError>
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onReconnect.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onReconnect = this.listeners[channel].onReconnect.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public addOnTickListener = (
        channel: string, 
        listener: IServerSentEventOnTick
    ): IServerSentEventUnsubscribe => {
        this.ensureChannel(channel);

        this.listeners[channel].onTick.push(listener);

        return () => {
            if (this.listeners[channel])
            {
                this.listeners[channel].onTick = this.listeners[channel].onTick.filter((x: any) => x !== listener);
            } 

            this.cleanChannel(channel);
        }
    };

    public dispose = () => {
        this.disposeClient();
    }
}

const ServerSentEventsProvider: FC<PropsWithChildren<IServerSentEventsProviderProps>> = ({ 
    children,
    baseUrl,
    debug
}) => {
    const manager = useMemo(() => new ServerSentEventsManager(baseUrl, debug === true), []);

    useEffect(() => {
        return () => {
            manager.dispose();
        }
    }, [manager])

    return (
        <ServerSentEventsContext.Provider value={manager}>
            {children}
        </ServerSentEventsContext.Provider>
    )
}

export default ServerSentEventsProvider